import { useState, useCallback } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { useCalendarStore } from '../store/calendarStore';
import type { EdgeCalendar, CalendarEvent, EventFormData } from '../types/calendar.types';

interface CreateCalendarDto {
  name: string;
  description?: string;
  color: string;
  is_default?: boolean;
}

interface ShareCalendarDto {
  user_id: string;
  permission_level: 'viewer' | 'editor' | 'owner';
}

interface EventFilters {
  start_date?: string;
  end_date?: string;
  calendar_ids?: string[];
  matter_id?: string;
  client_id?: string;
  priority?: string;
  event_type?: string;
}

interface CourtRuleTriggerInput {
  matterId: string;
  jurisdiction: string;
  court: string;
  triggerType: 'complaint_filed' | 'hearing_set' | 'order_entered';
  triggerDate: string;
}

type InviteRecipient = {
  email: string;
  name?: string;
};

type RsvpStatus = 'pending' | 'accepted' | 'declined' | 'tentative';

interface ImportIcsFromEdgeMailPayload {
  mailboxId: string;
  messageId: string;
  attachmentId?: string;
  calendarId?: string;
  syncTask?: boolean;
}

interface SendEventInvitesPayload {
  mailboxId: string;
  to?: InviteRecipient[];
  cc?: InviteRecipient[];
  bcc?: InviteRecipient[];
  message?: string;
  syncTask?: boolean;
}

interface UpdateAttendeeRsvpPayload {
  attendeeEmail: string;
  status: RsvpStatus;
  responseNote?: string;
  sourceMessageId?: string;
}

export const useCalendarApi = () => {
  const { keycloak } = useKeycloak();
  const { 
    calendars,
    events,
    setLoading, 
    setError,
    addCalendar,
    updateCalendar,
    deleteCalendar,
    addEvent,
    updateEvent,
    deleteEvent,
    setEvents
  } = useCalendarStore();
  
  const [localLoading, setLocalLoading] = useState(false);

  // Base API URL - use relative URLs for production NGINX proxy
  const API_BASE = '';

  const mapAttendee = (attendee: any) => ({
    id: attendee.id || `${attendee.event_id || attendee.eventId || 'event'}:${attendee.email || 'attendee'}`,
    eventId: attendee.event_id || attendee.eventId || '',
    userId: attendee.user_id || attendee.userId,
    clientId: attendee.client_id || attendee.clientId,
    contactId: attendee.contact_id || attendee.contactId || attendee.client_id || attendee.clientId,
    email: attendee.email || '',
    name: attendee.name || attendee.email || 'Attendee',
    phone: attendee.phone || undefined,
    organization: attendee.organization || undefined,
    role: attendee.role || undefined,
    status: attendee.status || 'pending',
    isOrganizer: attendee.is_organizer ?? attendee.isOrganizer ?? false,
    isRequired: attendee.is_required ?? attendee.isRequired ?? true,
    isPresenter: attendee.is_presenter ?? attendee.isPresenter ?? false,
    canModifyEvent: attendee.can_modify_event ?? attendee.canModifyEvent ?? false,
    responseTime: attendee.response_time ? new Date(attendee.response_time) : attendee.responseTime ? new Date(attendee.responseTime) : undefined,
    responseNote: attendee.response_note ?? attendee.responseNote,
    notificationPreferences: attendee.notification_preferences ?? attendee.notificationPreferences,
  });

  const mapReminder = (reminder: any) => ({
    id: reminder.id || `${reminder.event_id || reminder.eventId || 'event'}:reminder`,
    eventId: reminder.event_id || reminder.eventId || '',
    method: reminder.method || 'popup',
    timeValue: reminder.time_value ?? reminder.timeValue ?? 0,
    timeUnit: reminder.time_unit ?? reminder.timeUnit ?? 'minutes',
    isActive: reminder.is_active ?? reminder.isActive ?? true,
    status: reminder.status,
    scheduledTime: reminder.scheduled_time ? new Date(reminder.scheduled_time) : reminder.scheduledTime ? new Date(reminder.scheduledTime) : undefined,
    sentTime: reminder.sent_time ? new Date(reminder.sent_time) : reminder.sentTime ? new Date(reminder.sentTime) : undefined,
    failureReason: reminder.failure_reason ?? reminder.failureReason,
    content: reminder.content,
    deliveryMetadata: reminder.delivery_metadata ?? reminder.deliveryMetadata,
  });

  const transformBackendEvent = (backendEvent: any, fallbackEvent?: CalendarEvent): CalendarEvent => ({
    id: backendEvent.id,
    calendarId: backendEvent.calendar_id,
    title: backendEvent.title || 'Untitled Event',
    description: backendEvent.description || '',
    start: new Date(backendEvent.start_time),
    end: new Date(backendEvent.end_time),
    allDay: backendEvent.all_day || false,
    location: backendEvent.location || '',
    priority: backendEvent.priority || 'medium',
    eventTypeId: backendEvent.event_type || 'meeting',
    matterId: backendEvent.matter_id,
    clientId: backendEvent.client_id,
    attendees: Array.isArray(backendEvent.attendees)
      ? backendEvent.attendees.map(mapAttendee)
      : (fallbackEvent?.attendees || []),
    reminders: Array.isArray(backendEvent.reminders)
      ? backendEvent.reminders.map(mapReminder)
      : (fallbackEvent?.reminders || []),
    createdBy: backendEvent.created_by || fallbackEvent?.createdBy || 'unknown',
    createdAt: new Date(backendEvent.created_at || fallbackEvent?.createdAt || Date.now()),
    updatedAt: new Date(backendEvent.updated_at || Date.now()),
    isDeleted: backendEvent.is_deleted || false,
    recurringEventId: backendEvent.recurring_event_id,
    recurringRule: backendEvent.recurring_rule,
  });

  // Get JWT token from Keycloak (EXACT same pattern as contacts)
  const getAuthToken = useCallback(() => {
    console.log('🔑 [Calendar API] Getting token from React Keycloak provider...');
    
    // 1. First try the React Keycloak provider (most reliable)
    if (keycloak?.token) {
      console.log('✅ [Calendar API] Found token via React Keycloak provider');
      return keycloak.token;
    }
    
    // 2. Check if the global window object has the parsed token info
    const globalToken = (window as any).webelieveTokenParsed;
    if (globalToken) {
      console.log('🔍 [Calendar API] Found parsed token in window.webelieveTokenParsed:', globalToken);
      // If we have parsed token info, we need to reconstruct the JWT or find it elsewhere
    }
    
    // 3. Fall back to storage-based detection
    const jwtPattern = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
    
    console.log('🔍 [Calendar API] Falling back to storage detection...');
    for (const storage of [sessionStorage, localStorage]) {
      for (const key of Object.keys(storage)) {
        const value = storage.getItem(key);
        if (!value) continue;
        
        if (jwtPattern.test(value)) {
          console.log(`✅ [Calendar API] Found JWT token in ${storage === localStorage ? 'localStorage' : 'sessionStorage'}: ${key}`);
          return value;
        }
        
        if (value.startsWith('{')) {
          try {
            const parsed = JSON.parse(value);
            const tokenFields = ['token', 'access_token', 'accessToken', 'idToken', 'id_token'];
            for (const field of tokenFields) {
              if (parsed[field] && jwtPattern.test(parsed[field])) {
                console.log(`✅ [Calendar API] Found JWT token in ${storage === localStorage ? 'localStorage' : 'sessionStorage'} ${key}.${field}`);
                return parsed[field];
              }
            }
          } catch (e) { /* ignore */ }
        }
      }
    }
    
    console.log('❌ [Calendar API] No JWT token found');
    console.log('🔍 [Calendar API] Keycloak authenticated:', keycloak?.authenticated);
    console.log('🔍 [Calendar API] Keycloak instance:', !!keycloak);
    
    return '';
  }, [keycloak]);

  // Create headers with JWT authentication (EXACT same pattern as contacts)
  const getAuthHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${getAuthToken()}`
  }), [getAuthToken]);

  // ===== CALENDAR MANAGEMENT ENDPOINTS =====

  // Get all calendars for current user
  const fetchCalendars = useCallback(async (): Promise<EdgeCalendar[]> => {
    try {
      console.log('📋 [useCalendarApi] fetchCalendars called');
      setLocalLoading(true);
      setLoading(true);
      setError(null);

      const url = `${API_BASE}/api/v1/calendars`;
      const headers = getAuthHeaders();

      const response = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [useCalendarApi] Failed to fetch calendars:', errorText);
        throw new Error(`Failed to fetch calendars: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ [useCalendarApi] Calendars fetched successfully:', data);
      
      // Handle different possible response formats
      const calendarsArray = Array.isArray(data) ? data : data.calendars || data.data || [];
      
      // Convert backend format (snake_case) to frontend format (camelCase)
      const convertedCalendars = calendarsArray.map((calendar: any) => ({
        id: calendar.id,
        realmId: calendar.realm_id,
        name: calendar.name,
        color: calendar.color || '#3B82F6', // Default blue
        ownerId: calendar.owner_id,
        isDefault: calendar.is_default || false,
        isVisible: calendar.is_visible !== false, // Default to true unless explicitly false
        description: calendar.description || '',
        settings: calendar.settings || null,
        createdAt: new Date(calendar.created_at || Date.now()),
        updatedAt: new Date(calendar.updated_at || Date.now()),
        deletedAt: calendar.deleted_at ? new Date(calendar.deleted_at) : null
      }));
      
      console.log('🔄 [useCalendarApi] Converted calendars for frontend:', convertedCalendars);
      return convertedCalendars;
    } catch (error) {
      console.error('💥 [useCalendarApi] Failed to fetch calendars:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch calendars';
      setError(errorMessage);
      throw error;
    } finally {
      setLocalLoading(false);
      setLoading(false);
    }
  }, [API_BASE, getAuthHeaders]);

  // Create new calendar
  const createCalendarApi = useCallback(async (calendarData: CreateCalendarDto): Promise<EdgeCalendar> => {
    try {
      console.log('🔍 [useCalendarApi] createCalendar called with data:', calendarData);
      setLocalLoading(true);
      setLoading(true);
      setError(null);

      const url = `${API_BASE}/api/v1/calendars`;
      const headers = getAuthHeaders();
      
      console.log('🔄 [useCalendarApi] Making create calendar API request:', { url });

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(calendarData)
      });

      console.log('📡 [useCalendarApi] API Response:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [useCalendarApi] API Error Response:', errorText);
        throw new Error(`Failed to create calendar: ${response.status} ${response.statusText}`);
      }

      const newCalendar = await response.json();
      console.log('✅ [useCalendarApi] Calendar created successfully:', newCalendar);
      
      // Update local state
      addCalendar(newCalendar);
      
      return newCalendar;
    } catch (error) {
      console.error('💥 [useCalendarApi] Failed to create calendar:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create calendar';
      setError(errorMessage);
      throw error;
    } finally {
      setLocalLoading(false);
      setLoading(false);
    }
  }, [addCalendar, API_BASE, getAuthHeaders]);

  // Update calendar
  const updateCalendarApi = useCallback(async (calendarId: string, updates: Partial<CreateCalendarDto>): Promise<EdgeCalendar> => {
    try {
      console.log('🔍 [useCalendarApi] updateCalendar called:', { calendarId, updates });
      setLocalLoading(true);
      setLoading(true);
      setError(null);

      const url = `${API_BASE}/api/v1/calendars/${calendarId}`;
      const headers = getAuthHeaders();

      const response = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [useCalendarApi] Failed to update calendar:', errorText);
        throw new Error(`Failed to update calendar: ${response.status} ${response.statusText}`);
      }

      const updatedCalendar = await response.json();
      console.log('✅ [useCalendarApi] Calendar updated successfully:', updatedCalendar);
      
      // Update local state
      updateCalendar(calendarId, updatedCalendar);
      
      return updatedCalendar;
    } catch (error) {
      console.error('💥 [useCalendarApi] Failed to update calendar:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update calendar';
      setError(errorMessage);
      throw error;
    } finally {
      setLocalLoading(false);
      setLoading(false);
    }
  }, [updateCalendar, API_BASE, getAuthHeaders]);

  // Delete calendar
  const deleteCalendarApi = useCallback(async (calendarId: string): Promise<void> => {
    try {
      console.log('🔍 [useCalendarApi] deleteCalendar called:', calendarId);
      setLocalLoading(true);
      setLoading(true);
      setError(null);

      const url = `${API_BASE}/api/v1/calendars/${calendarId}`;
      const headers = getAuthHeaders();

      const response = await fetch(url, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [useCalendarApi] Failed to delete calendar:', errorText);
        throw new Error(`Failed to delete calendar: ${response.status} ${response.statusText}`);
      }

      console.log('✅ [useCalendarApi] Calendar deleted successfully');
      
      // Update local state
      deleteCalendar(calendarId);
    } catch (error) {
      console.error('💥 [useCalendarApi] Failed to delete calendar:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete calendar';
      setError(errorMessage);
      throw error;
    } finally {
      setLocalLoading(false);
      setLoading(false);
    }
  }, [deleteCalendar, API_BASE, getAuthHeaders]);

  // ===== EVENT MANAGEMENT ENDPOINTS =====

  // Get all events with optional filters
  const fetchEvents = useCallback(async (filters?: EventFilters): Promise<CalendarEvent[]> => {
    try {
      console.log('📝 [useCalendarApi] fetchEvents called with filters:', filters);
      setLocalLoading(true);
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (filters?.start_date) params.append('start_date', filters.start_date);
      if (filters?.end_date) params.append('end_date', filters.end_date);
      if (filters?.calendar_ids) params.append('calendar_ids', filters.calendar_ids.join(','));
      if (filters?.matter_id) params.append('matter_id', filters.matter_id);
      if (filters?.client_id) params.append('client_id', filters.client_id);
      if (filters?.priority) params.append('priority', filters.priority);
      if (filters?.event_type) params.append('event_type', filters.event_type);

      const url = `${API_BASE}/api/v1/calendars/events${params.toString() ? `?${params.toString()}` : ''}`;
      const headers = getAuthHeaders();

      const response = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [useCalendarApi] Failed to fetch events:', errorText);
        throw new Error(`Failed to fetch events: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ [useCalendarApi] Events fetched successfully:', data);
      
      const eventsArray = Array.isArray(data) ? data : data.events || data.data || [];
      
      // Convert backend format (snake_case) to frontend format (camelCase)
      const convertedEvents = eventsArray.map((event: any) => ({
        id: event.id,
        calendarId: event.calendar_id, // Convert snake_case to camelCase
        title: event.title || 'Untitled Event',
        description: event.description || '',
        start: new Date(event.start_time), // Convert string to Date
        end: new Date(event.end_time), // Convert string to Date
        allDay: event.all_day || false,
        location: event.location || '',
        priority: event.priority || 'medium',
        eventTypeId: event.event_type || 'meeting',
        matterId: event.matter_id,
        clientId: event.client_id,
        attendees: event.attendees || [],
        reminders: event.reminders || [],
        recurrence: event.recurrence || null,
        createdBy: event.created_by || 'unknown',
        createdAt: new Date(event.created_at || Date.now()),
        updatedAt: new Date(event.updated_at || Date.now()),
        isDeleted: false
      }));
      
      console.log('🔄 [useCalendarApi] Converted events for frontend:', convertedEvents);
      return convertedEvents;
    } catch (error) {
      console.error('💥 [useCalendarApi] Failed to fetch events:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch events';
      setError(errorMessage);
      throw error;
    } finally {
      setLocalLoading(false);
      setLoading(false);
    }
  }, [API_BASE, getAuthHeaders]);

  // Get events for specific calendar
  const fetchCalendarEvents = useCallback(async (calendarId: string): Promise<CalendarEvent[]> => {
    try {
      console.log('📝 [useCalendarApi] fetchCalendarEvents called:', calendarId);
      setLocalLoading(true);
      setLoading(true);
      setError(null);

      const url = `${API_BASE}/api/v1/calendars/${calendarId}/events`;
      const headers = getAuthHeaders();

      const response = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [useCalendarApi] Failed to fetch calendar events:', errorText);
        throw new Error(`Failed to fetch calendar events: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ [useCalendarApi] Calendar events fetched successfully:', data);
      
      const eventsArray = Array.isArray(data) ? data : data.events || data.data || [];
      
      // Convert backend format (snake_case) to frontend format (camelCase)
      const convertedEvents = eventsArray.map((event: any) => ({
        id: event.id,
        calendarId: event.calendar_id, // Convert snake_case to camelCase
        title: event.title || 'Untitled Event',
        description: event.description || '',
        start: new Date(event.start_time), // Convert string to Date
        end: new Date(event.end_time), // Convert string to Date
        allDay: event.all_day || false,
        location: event.location || '',
        priority: event.priority || 'medium',
        eventTypeId: event.event_type || 'meeting',
        matterId: event.matter_id,
        clientId: event.client_id,
        attendees: event.attendees || [],
        reminders: event.reminders || [],
        recurrence: event.recurrence || null,
        createdBy: event.created_by || 'unknown',
        createdAt: new Date(event.created_at || Date.now()),
        updatedAt: new Date(event.updated_at || Date.now()),
        isDeleted: false
      }));
      
      console.log('🔄 [useCalendarApi] Converted calendar events for frontend:', convertedEvents);
      return convertedEvents;
    } catch (error) {
      console.error('💥 [useCalendarApi] Failed to fetch calendar events:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch calendar events';
      setError(errorMessage);
      throw error;
    } finally {
      setLocalLoading(false);
      setLoading(false);
    }
  }, [API_BASE, getAuthHeaders]);

  // Create new event
  const createEventApi = useCallback(async (eventData: Partial<EventFormData>): Promise<CalendarEvent> => {
    try {
      console.log('🔍 [useCalendarApi] createEvent called with data:', eventData);
      
      if (!eventData.calendarId) {
        throw new Error('Calendar ID is required - eventData.calendarId is missing');
      }
      
      setLocalLoading(true);
      setLoading(true);
      setError(null);

      // Validate that we have a real calendar (not a mock one)
      const calendar = calendars.find(cal => cal.id === eventData.calendarId);
      if (!calendar) {
        console.error('❌ [useCalendarApi] Calendar not found:', eventData.calendarId);
        console.log('🔍 [useCalendarApi] Available calendars:', calendars);
        throw new Error(`Calendar with ID ${eventData.calendarId} not found. Please select a valid calendar.`);
      }
      
      console.log('✅ [useCalendarApi] Using valid calendar:', calendar);

      // Format data for backend API (as tested in calendar-api-tester.js)
      // Only send properties that the backend expects - let backend generate UUIDs
      const backendData: any = {
        calendar_id: eventData.calendarId,
        title: eventData.title || 'Untitled Event',
        description: eventData.description || '',
        location: eventData.location || '',
        priority: eventData.priority || 'medium',
        all_day: eventData.allDay || false,
        event_type: eventData.eventTypeId || 'meeting', // Use form data eventTypeId
      };

      // Format dates properly for backend - ensure they're valid
      if (eventData.start) {
        const startDate = eventData.start instanceof Date ? eventData.start : new Date(eventData.start);
        if (isNaN(startDate.getTime())) {
          throw new Error('Invalid start date provided');
        }
        backendData.start_time = startDate.toISOString();
      } else {
        throw new Error('Start time is required');
      }
      
      if (eventData.end) {
        const endDate = eventData.end instanceof Date ? eventData.end : new Date(eventData.end);
        if (isNaN(endDate.getTime())) {
          throw new Error('Invalid end date provided');
        }
        backendData.end_time = endDate.toISOString();
      } else {
        throw new Error('End time is required');
      }

      // Add optional matter/client linking (only if they're valid UUIDs)
      if (eventData.matterId && eventData.matterId.trim()) {
        backendData.matter_id = eventData.matterId;
      }
      
      if (eventData.contactIds && eventData.contactIds.length > 0 && eventData.contactIds[0].trim()) {
        backendData.client_id = eventData.contactIds[0];
      }
      
      console.log('🔄 [useCalendarApi] Formatted data for backend:', backendData);
      console.log('🔄 [useCalendarApi] Data validation:', {
        hasTitle: !!backendData.title,
        hasCalendarId: !!backendData.calendar_id,
        hasStartTime: !!backendData.start_time,
        hasEndTime: !!backendData.end_time,
        startTimeValid: !isNaN(new Date(backendData.start_time).getTime()),
        endTimeValid: !isNaN(new Date(backendData.end_time).getTime())
      });
      
      const url = `${API_BASE}/api/v1/calendars/events`;
      const headers = getAuthHeaders();

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(backendData)
      });

      console.log('📡 [useCalendarApi] API Response:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [useCalendarApi] API Error Response:', errorText);
        throw new Error(`Failed to create event: ${response.status} ${response.statusText}`);
      }

      const newEvent = await response.json();
      console.log('✅ [useCalendarApi] Event created successfully:', newEvent);
      
      // Check event format before adding to store
      console.log('🔍 [useCalendarApi] Event format check:', {
        hasId: !!newEvent.id,
        hasCalendarId: !!newEvent.calendar_id,
        hasTitle: !!newEvent.title,
        hasStartTime: !!newEvent.start_time,
        hasEndTime: !!newEvent.end_time,
        rawCalendarId: newEvent.calendar_id,
        rawStartTime: newEvent.start_time,
        rawEndTime: newEvent.end_time
      });
      
      // Convert backend format (snake_case) to frontend format (camelCase)
      const frontendEvent = {
        id: newEvent.id,
        calendarId: newEvent.calendar_id, // Convert snake_case to camelCase
        title: newEvent.title,
        description: newEvent.description || '',
        start: new Date(newEvent.start_time), // Convert string to Date
        end: new Date(newEvent.end_time), // Convert string to Date
        allDay: newEvent.all_day || false,
        location: newEvent.location || '',
        priority: newEvent.priority || 'medium',
        eventTypeId: newEvent.event_type || 'meeting',
        matterId: newEvent.matter_id,
        clientId: newEvent.client_id,
        attendees: newEvent.attendees || [],
        reminders: newEvent.reminders || [],
        recurrence: newEvent.recurrence || null,
        createdBy: newEvent.created_by || 'unknown', // Add missing createdBy field
        createdAt: new Date(newEvent.created_at || Date.now()),
        updatedAt: new Date(newEvent.updated_at || Date.now()),
        isDeleted: false
      };
      
      console.log('🔄 [useCalendarApi] Converted event for frontend:', frontendEvent);
      console.log('📋 [useCalendarApi] Current visible calendar IDs in store:', calendars.map(c => ({ id: c.id, name: c.name })));
      console.log('🎯 [useCalendarApi] Event calendar ID:', frontendEvent.calendarId);
      
      // Update local state
      addEvent(frontendEvent);
      
      console.log('✅ [useCalendarApi] Event added to store, total events now:', events.length + 1);
      
      // Refresh events to ensure we have the latest data
      console.log('🔄 [useCalendarApi] Refreshing all events after creation...');
      try {
        const refreshedEvents = await fetchEvents();
        if (refreshedEvents && Array.isArray(refreshedEvents)) {
          setEvents(refreshedEvents);
          console.log('✅ [useCalendarApi] Events refreshed, total count:', refreshedEvents.length);
        }
      } catch (refreshError) {
        console.warn('⚠️ [useCalendarApi] Failed to refresh events after creation:', refreshError);
      }
      
      return frontendEvent;
    } catch (error) {
      console.error('💥 [useCalendarApi] Failed to create event:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create event';
      setError(errorMessage);
      throw error;
    } finally {
      setLocalLoading(false);
      setLoading(false);
    }
  }, [addEvent, setLoading, setError, API_BASE, getAuthHeaders]);

  // Update event
  const updateEventApi = useCallback(async (eventId: string, updates: Partial<EventFormData>): Promise<CalendarEvent> => {
    try {
      console.log('🔍 [useCalendarApi] updateEvent called:', { eventId, updates });
      setLocalLoading(true);
      setLoading(true);
      setError(null);

      // Format updates for backend API
      const backendUpdates: any = {};
      if (updates.title !== undefined) backendUpdates.title = updates.title;
      if (updates.description !== undefined) backendUpdates.description = updates.description;
      if (updates.start !== undefined) backendUpdates.start_time = updates.start;
      if (updates.end !== undefined) backendUpdates.end_time = updates.end;
      if (updates.location !== undefined) backendUpdates.location = updates.location;
      if (updates.priority !== undefined) backendUpdates.priority = updates.priority;
      if (updates.allDay !== undefined) backendUpdates.all_day = updates.allDay;
      if (updates.eventTypeId !== undefined) backendUpdates.event_type = updates.eventTypeId;

      const existingEvent = events.find((event) => event.id === eventId);
      if (existingEvent?.updatedAt) {
        backendUpdates.expected_updated_at = existingEvent.updatedAt.toISOString();
      }

      const url = `${API_BASE}/api/v1/calendars/events/${eventId}`;
      const headers = getAuthHeaders();

      const response = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(backendUpdates)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [useCalendarApi] Failed to update event:', errorText);
        if (response.status === 409) {
          throw new Error('This event was updated by another user. Refresh and retry your changes.');
        }
        throw new Error(`Failed to update event: ${response.status} ${response.statusText}`);
      }

      const backendEvent = await response.json();
      console.log('✅ [useCalendarApi] Event updated successfully:', backendEvent);
      
      // Transform backend response to frontend format (same as fetchEvents)
      const frontendEvent = {
        id: backendEvent.id,
        calendarId: backendEvent.calendar_id,
        title: backendEvent.title || 'Untitled Event',
        description: backendEvent.description || '',
        start: new Date(backendEvent.start_time),
        end: new Date(backendEvent.end_time),
        allDay: backendEvent.all_day || false,
        location: backendEvent.location || '',
        priority: backendEvent.priority || 'medium',
        eventTypeId: backendEvent.event_type || 'meeting', // ✅ Key fix: transform event_type → eventTypeId
        matterId: backendEvent.matter_id,
        clientId: backendEvent.client_id,
        attendees: backendEvent.attendees || [],
        createdBy: backendEvent.created_by,
        createdAt: new Date(backendEvent.created_at || Date.now()),
        updatedAt: new Date(backendEvent.updated_at || Date.now()),
        isDeleted: backendEvent.is_deleted || false,
        recurringEventId: backendEvent.recurring_event_id,
        recurringRule: backendEvent.recurring_rule
      };
      
      console.log('🔄 [useCalendarApi] Transformed event for frontend store:', frontendEvent);
      
      // Update local state with properly formatted event
      updateEvent(eventId, frontendEvent);
      
      return frontendEvent;
    } catch (error) {
      console.error('💥 [useCalendarApi] Failed to update event:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update event';
      setError(errorMessage);
      throw error;
    } finally {
      setLocalLoading(false);
      setLoading(false);
    }
  }, [updateEvent, API_BASE, getAuthHeaders]);

  // Delete event
  const deleteEventApi = useCallback(async (eventId: string): Promise<void> => {
    try {
      console.log('🔍 [useCalendarApi] deleteEvent called:', eventId);
      setLocalLoading(true);
      setLoading(true);
      setError(null);

      const url = `${API_BASE}/api/v1/calendars/events/${eventId}`;
      const headers = getAuthHeaders();

      const response = await fetch(url, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [useCalendarApi] Failed to delete event:', errorText);
        throw new Error(`Failed to delete event: ${response.status} ${response.statusText}`);
      }

      console.log('✅ [useCalendarApi] Event deleted successfully');
      
      // Update local state
      deleteEvent(eventId);
    } catch (error) {
      console.error('💥 [useCalendarApi] Failed to delete event:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete event';
      setError(errorMessage);
      throw error;
    } finally {
      setLocalLoading(false);
      setLoading(false);
    }
  }, [deleteEvent, API_BASE, getAuthHeaders]);

  // ===== MATTER/CLIENT INTEGRATION ENDPOINTS =====

  // Get events for specific matter
  const fetchMatterEvents = useCallback(async (matterId: string): Promise<CalendarEvent[]> => {
    try {
      console.log('📁 [useCalendarApi] fetchMatterEvents called:', matterId);
      setLocalLoading(true);
      setLoading(true);
      setError(null);

      const url = `${API_BASE}/api/v1/calendars/matters/${matterId}/events`;
      const headers = getAuthHeaders();

      const response = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [useCalendarApi] Failed to fetch matter events:', errorText);
        throw new Error(`Failed to fetch matter events: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ [useCalendarApi] Matter events fetched successfully:', data);
      
      const eventsArray = Array.isArray(data) ? data : data.data || [];
      
      // Convert backend format (snake_case) to frontend format (camelCase)
      const convertedEvents = eventsArray.map((event: any) => ({
        id: event.id,
        calendarId: event.calendar_id,
        title: event.title || 'Untitled Event',
        description: event.description || '',
        start: new Date(event.start_time),
        end: new Date(event.end_time),
        allDay: event.all_day || false,
        location: event.location || '',
        priority: event.priority || 'medium',
        eventTypeId: event.event_type || 'meeting',
        matterId: event.matter_id,
        clientId: event.client_id,
        attendees: event.attendees || [],
        reminders: event.reminders || [],
        recurrence: event.recurrence || null,
        createdBy: event.created_by || 'unknown',
        createdAt: new Date(event.created_at || Date.now()),
        updatedAt: new Date(event.updated_at || Date.now()),
        isDeleted: false
      }));
      
      return convertedEvents;
    } catch (error) {
      console.error('💥 [useCalendarApi] Failed to fetch matter events:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch matter events';
      setError(errorMessage);
      throw error;
    } finally {
      setLocalLoading(false);
      setLoading(false);
    }
  }, [API_BASE, getAuthHeaders]);

  // Get events for specific client
  const fetchClientEvents = useCallback(async (clientId: string): Promise<CalendarEvent[]> => {
    try {
      console.log('👤 [useCalendarApi] fetchClientEvents called:', clientId);
      setLocalLoading(true);
      setLoading(true);
      setError(null);

      const url = `${API_BASE}/api/v1/calendars/clients/${clientId}/events`;
      const headers = getAuthHeaders();

      const response = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [useCalendarApi] Failed to fetch client events:', errorText);
        throw new Error(`Failed to fetch client events: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ [useCalendarApi] Client events fetched successfully:', data);
      
      const eventsArray = Array.isArray(data) ? data : data.data || [];
      
      // Convert backend format (snake_case) to frontend format (camelCase)
      const convertedEvents = eventsArray.map((event: any) => ({
        id: event.id,
        calendarId: event.calendar_id,
        title: event.title || 'Untitled Event',
        description: event.description || '',
        start: new Date(event.start_time),
        end: new Date(event.end_time),
        allDay: event.all_day || false,
        location: event.location || '',
        priority: event.priority || 'medium',
        eventTypeId: event.event_type || 'meeting',
        matterId: event.matter_id,
        clientId: event.client_id,
        attendees: event.attendees || [],
        reminders: event.reminders || [],
        recurrence: event.recurrence || null,
        createdBy: event.created_by || 'unknown',
        createdAt: new Date(event.created_at || Date.now()),
        updatedAt: new Date(event.updated_at || Date.now()),
        isDeleted: false
      }));
      
      return convertedEvents;
    } catch (error) {
      console.error('💥 [useCalendarApi] Failed to fetch client events:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch client events';
      setError(errorMessage);
      throw error;
    } finally {
      setLocalLoading(false);
      setLoading(false);
    }
  }, [API_BASE, getAuthHeaders]);

  // ===== PERMISSIONS & SHARING ENDPOINTS =====

  // Get calendar permissions
  const fetchCalendarPermissions = useCallback(async (calendarId: string) => {
    try {
      console.log('🔐 [useCalendarApi] fetchCalendarPermissions called:', calendarId);
      setLocalLoading(true);
      setLoading(true);
      setError(null);

      const url = `${API_BASE}/api/v1/calendars/${calendarId}/permissions`;
      const headers = getAuthHeaders();

      const response = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [useCalendarApi] Failed to fetch calendar permissions:', errorText);
        throw new Error(`Failed to fetch calendar permissions: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ [useCalendarApi] Calendar permissions fetched successfully:', data);
      
      return data;
    } catch (error) {
      console.error('💥 [useCalendarApi] Failed to fetch calendar permissions:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch calendar permissions';
      setError(errorMessage);
      throw error;
    } finally {
      setLocalLoading(false);
      setLoading(false);
    }
  }, [API_BASE, getAuthHeaders]);

  // Share calendar
  const shareCalendarApi = useCallback(async (calendarId: string, shareData: ShareCalendarDto) => {
    try {
      console.log('🤝 [useCalendarApi] shareCalendar called:', { calendarId, shareData });
      setLocalLoading(true);
      setLoading(true);
      setError(null);

      const url = `${API_BASE}/api/v1/calendars/${calendarId}/share`;
      const headers = getAuthHeaders();

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(shareData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [useCalendarApi] Failed to share calendar:', errorText);
        throw new Error(`Failed to share calendar: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ [useCalendarApi] Calendar shared successfully:', data);
      
      return data;
    } catch (error) {
      console.error('💥 [useCalendarApi] Failed to share calendar:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to share calendar';
      setError(errorMessage);
      throw error;
    } finally {
      setLocalLoading(false);
      setLoading(false);
    }
  }, [API_BASE, getAuthHeaders]);

  // ===== COURT RULES STUB ENDPOINTS =====

  const fetchCourtRuleSources = useCallback(async () => {
    try {
      setLocalLoading(true);
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE}/api/v1/calendars/court-rules/sources`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch court rule sources: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch court rule sources';
      setError(errorMessage);
      throw error;
    } finally {
      setLocalLoading(false);
      setLoading(false);
    }
  }, [API_BASE, getAuthHeaders]);

  const previewCourtRuleCascade = useCallback(async (payload: CourtRuleTriggerInput) => {
    try {
      setLocalLoading(true);
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE}/api/v1/calendars/court-rules/preview`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to preview court rule cascade: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to preview court rule cascade';
      setError(errorMessage);
      throw error;
    } finally {
      setLocalLoading(false);
      setLoading(false);
    }
  }, [API_BASE, getAuthHeaders]);

  const applyCourtRuleCascade = useCallback(async (payload: CourtRuleTriggerInput) => {
    try {
      setLocalLoading(true);
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE}/api/v1/calendars/court-rules/apply`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let detail = '';
        try {
          const errorPayload = await response.json();
          detail = (errorPayload?.message || errorPayload?.description || errorPayload?.error || '').toString();
        } catch {
          // Ignore JSON parse errors and fallback to status text.
        }
        const suffix = detail ? ` - ${detail}` : '';
        throw new Error(`Failed to apply court rule cascade: ${response.status} ${response.statusText}${suffix}`);
      }

      return await response.json();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to apply court rule cascade';
      setError(errorMessage);
      throw error;
    } finally {
      setLocalLoading(false);
      setLoading(false);
    }
  }, [API_BASE, getAuthHeaders]);

  // ===== PHASE 3 INTEGRATION ENDPOINTS =====

  const importIcsFromEdgeMailMessage = useCallback(async (
    payload: ImportIcsFromEdgeMailPayload,
  ): Promise<{ action: string; event: CalendarEvent; attendee?: any }> => {
    try {
      setLocalLoading(true);
      setLoading(true);
      setError(null);

      const url = `${API_BASE}/api/v1/calendars/integrations/edgemail/mailboxes/${encodeURIComponent(payload.mailboxId)}/messages/${encodeURIComponent(payload.messageId)}/import-ics`;
      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          attachment_id: payload.attachmentId,
          calendar_id: payload.calendarId,
          sync_task: payload.syncTask === true,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to import ICS: ${response.status} ${response.statusText} ${errorText}`);
      }

      const data = await response.json();
      const existingEvent = data?.event?.id ? events.find((event) => event.id === data.event.id) : undefined;
      const frontendEvent = transformBackendEvent(data.event, existingEvent);

      if (existingEvent) {
        updateEvent(existingEvent.id, frontendEvent);
      } else {
        addEvent(frontendEvent);
      }

      return {
        ...data,
        event: frontendEvent,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to import ICS';
      setError(errorMessage);
      throw error;
    } finally {
      setLocalLoading(false);
      setLoading(false);
    }
  }, [API_BASE, addEvent, events, getAuthHeaders, setError, setLoading, transformBackendEvent, updateEvent]);

  const sendEventInvites = useCallback(async (
    eventId: string,
    payload: SendEventInvitesPayload,
  ): Promise<{ event: CalendarEvent; queueId?: string; messageId?: string; uid?: string; sequence?: number }> => {
    try {
      setLocalLoading(true);
      setLoading(true);
      setError(null);

      const url = `${API_BASE}/api/v1/calendars/events/${encodeURIComponent(eventId)}/send-invites`;
      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          mailbox_id: payload.mailboxId,
          to: payload.to,
          cc: payload.cc,
          bcc: payload.bcc,
          message: payload.message,
          sync_task: payload.syncTask === true,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to send invites: ${response.status} ${response.statusText} ${errorText}`);
      }

      const data = await response.json();
      const existingEvent = events.find((event) => event.id === eventId);
      let frontendEvent = transformBackendEvent(data.event, existingEvent);

      if ((!frontendEvent.attendees || frontendEvent.attendees.length === 0) && data?.event?.id) {
        const fallbackAttendees = [
          ...(payload.to || []).map((recipient: any) => ({
            id: `${data.event.id}:${String(recipient.email).trim().toLowerCase()}`,
            eventId: data.event.id,
            email: String(recipient.email || '').trim().toLowerCase(),
            name: String(recipient.name || recipient.email || '').trim() || String(recipient.email || '').trim().toLowerCase(),
            status: 'pending' as const,
            isOrganizer: false,
            isRequired: true,
            isPresenter: false,
            canModifyEvent: false,
          })),
          ...(payload.cc || []).map((recipient: any) => ({
            id: `${data.event.id}:${String(recipient.email).trim().toLowerCase()}`,
            eventId: data.event.id,
            email: String(recipient.email || '').trim().toLowerCase(),
            name: String(recipient.name || recipient.email || '').trim() || String(recipient.email || '').trim().toLowerCase(),
            status: 'pending' as const,
            isOrganizer: false,
            isRequired: false,
            isPresenter: false,
            canModifyEvent: false,
          })),
        ].filter((attendee) => attendee.email);

        if (fallbackAttendees.length > 0) {
          const deduped = new Map<string, typeof fallbackAttendees[number]>();
          for (const attendee of fallbackAttendees) {
            const existing = deduped.get(attendee.email);
            if (!existing) {
              deduped.set(attendee.email, attendee);
            } else if (existing.isRequired === false && attendee.isRequired === true) {
              deduped.set(attendee.email, attendee);
            }
          }
          frontendEvent = {
            ...frontendEvent,
            attendees: Array.from(deduped.values()),
          };
        }
      }

      if (existingEvent) {
        updateEvent(existingEvent.id, frontendEvent);
      } else {
        addEvent(frontendEvent);
      }

      return {
        event: frontendEvent,
        queueId: data.queueId || data.queue_id,
        messageId: data.messageId || data.message_id,
        uid: data.uid,
        sequence: data.sequence,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send invites';
      setError(errorMessage);
      throw error;
    } finally {
      setLocalLoading(false);
      setLoading(false);
    }
  }, [API_BASE, addEvent, events, getAuthHeaders, setError, setLoading, transformBackendEvent, updateEvent]);

  const updateAttendeeRsvp = useCallback(async (
    eventId: string,
    payload: UpdateAttendeeRsvpPayload,
  ): Promise<{ event: CalendarEvent; attendee?: any }> => {
    try {
      setLocalLoading(true);
      setLoading(true);
      setError(null);

      const url = `${API_BASE}/api/v1/calendars/events/${encodeURIComponent(eventId)}/attendees/rsvp`;
      const response = await fetch(url, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          attendee_email: payload.attendeeEmail,
          status: payload.status,
          response_note: payload.responseNote,
          source_message_id: payload.sourceMessageId,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update RSVP: ${response.status} ${response.statusText} ${errorText}`);
      }

      const data = await response.json();
      const existingEvent = events.find((event) => event.id === eventId);
      let frontendEvent = transformBackendEvent(data.event, existingEvent);

      if (data.attendee) {
        const normalizedEmail = String(payload.attendeeEmail || '').trim().toLowerCase();
        const attendeeFromResponse = mapAttendee(data.attendee);
        const attendees = [...(frontendEvent.attendees || [])];
        const existingIndex = attendees.findIndex(
          (attendee) => attendee.email.trim().toLowerCase() === normalizedEmail,
        );

        const mergedAttendee = {
          ...attendeeFromResponse,
          email: payload.attendeeEmail,
          status: payload.status,
          responseNote: payload.responseNote ?? attendeeFromResponse.responseNote,
          responseTime: new Date(),
        };

        if (existingIndex >= 0) {
          attendees[existingIndex] = {
            ...attendees[existingIndex],
            ...mergedAttendee,
          };
        } else {
          attendees.push(mergedAttendee);
        }

        frontendEvent = {
          ...frontendEvent,
          attendees,
          updatedAt: new Date(),
        };
      }

      if (existingEvent) {
        updateEvent(existingEvent.id, frontendEvent);
      } else {
        addEvent(frontendEvent);
      }

      return {
        event: frontendEvent,
        attendee: data.attendee,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update RSVP';
      setError(errorMessage);
      throw error;
    } finally {
      setLocalLoading(false);
      setLoading(false);
    }
  }, [API_BASE, addEvent, events, getAuthHeaders, setError, setLoading, transformBackendEvent, updateEvent]);

  return {
    // Data
    calendars,
    events,
    loading: localLoading,
    
    // Calendar Management
    fetchCalendars,
    createCalendar: createCalendarApi,
    updateCalendar: updateCalendarApi,
    deleteCalendar: deleteCalendarApi,
    
    // Event Management
    fetchEvents,
    fetchCalendarEvents,
    createEvent: createEventApi,
    updateEvent: updateEventApi,
    deleteEvent: deleteEventApi,
    
    // Matter/Client Integration
    fetchMatterEvents,
    fetchClientEvents,
    
    // Permissions & Sharing
    fetchCalendarPermissions,
    shareCalendar: shareCalendarApi,

    // Court Rules
    fetchCourtRuleSources,
    previewCourtRuleCascade,
    applyCourtRuleCascade,

    // Phase 3 Integrations
    importIcsFromEdgeMailMessage,
    sendEventInvites,
    updateAttendeeRsvp,
  };
};
